import { useState, type PointerEvent } from 'react';
import type { TrendPoint } from '../sections/cluster-summary';

function time(at: number): string {
  return new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function segments(points: TrendPoint[]): TrendPoint[][] {
  const runs: TrendPoint[][] = [[]];
  for (const point of points) {
    if (point.value === null) {
      if (runs[runs.length - 1].length) runs.push([]);
    } else runs[runs.length - 1].push(point);
  }
  return runs.filter(run => run.length);
}

/**
 * One figure over time on a fixed 0-to-`max` scale, so a full line means the
 * limit is reached. Gaps are samples the source did not report. Hovering
 * shows the value at that moment.
 */
export function Sparkline({
  label,
  points,
  max,
  format,
  height = 32,
}: {
  label: string;
  points: TrendPoint[];
  max: number;
  format: (value: number) => string;
  height?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const known = points.filter(point => point.value !== null);
  if (points.length < 2 || !known.length || max <= 0)
    return (
      <div className="flex items-center text-xs text-muted-foreground" style={{ height }}>
        Collecting history
      </div>
    );

  const start = points[0].at;
  const span = Math.max(1, points[points.length - 1].at - start);
  const x = (at: number) => ((at - start) / span) * 100;
  const y = (value: number) => height - Math.min(1, value / max) * (height - 2) - 1;
  const minutes = Math.max(1, Math.round(span / 60_000));
  const now = known[known.length - 1].value!;
  const peak = Math.max(...known.map(point => point.value!));

  function onPointerMove(event: PointerEvent<SVGSVGElement>) {
    const box = event.currentTarget.getBoundingClientRect();
    const at = start + ((event.clientX - box.left) / box.width) * span;
    let nearest = 0;
    for (let index = 1; index < points.length; index += 1)
      if (Math.abs(points[index].at - at) < Math.abs(points[nearest].at - at)) nearest = index;
    setHover(nearest);
  }

  const hovered = hover === null ? null : points[hover];
  return (
    <div className="relative">
      <svg
        role="img"
        aria-label={`${label}, last ${minutes} min: now ${format(now)}, peak ${format(peak)}`}
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        className="block w-full overflow-visible"
        style={{ height }}
        onPointerMove={onPointerMove}
        onPointerLeave={() => setHover(null)}
      >
        {segments(points).map(run => {
          const line = run
            .map((point, index) => `${index ? 'L' : 'M'}${x(point.at)} ${y(point.value!)}`)
            .join(' ');
          return (
            <g key={run[0].at}>
              <path
                d={`${line} L${x(run[run.length - 1].at)} ${height} L${x(run[0].at)} ${height} Z`}
                className="fill-primary/10"
              />
              <path
                d={line}
                fill="none"
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                className="stroke-primary"
              />
            </g>
          );
        })}
        {hovered && (
          <line
            x1={x(hovered.at)}
            x2={x(hovered.at)}
            y1={0}
            y2={height}
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
            className="stroke-muted-foreground/50"
          />
        )}
      </svg>
      {hovered && (
        <>
          {hovered.value !== null && (
            <span
              aria-hidden
              className="pointer-events-none absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary ring-2 ring-background"
              style={{ left: `${x(hovered.at)}%`, top: y(hovered.value) }}
            />
          )}
          <div
            aria-hidden
            className="pointer-events-none absolute bottom-full z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md border bg-popover px-2 py-1 text-xs text-popover-foreground shadow-sm tabular-nums"
            style={{ left: `${Math.min(85, Math.max(15, x(hovered.at)))}%` }}
          >
            {time(hovered.at)} · {hovered.value === null ? 'No data' : format(hovered.value)}
          </div>
        </>
      )}
    </div>
  );
}
